import React, { useState, useEffect } from 'react';
import { quotesAPI, clientsAPI } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import {
  FileText, Plus, ArrowLeft, Loader2, FileCheck, FilePlus2,
  Calendar, Euro, Trash2, Send, CheckCircle, XCircle, Clock,
  ArrowRightCircle, RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const QuotesPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, quote: null });
  const [convertConfirm, setConvertConfirm] = useState({ open: false, quote: null });
  const [deleting, setDeleting] = useState(false);
  const [converting, setConverting] = useState(false);

  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    client_email: '',
    client_address: '',
    client_siren: '',
    validity_days: 30,
    payment_terms: 'Net 30',
    notes: '',
    items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 20 }]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [quotesRes, clientsRes, statsRes] = await Promise.all([
        quotesAPI.getAll(),
        clientsAPI.getAll(),
        quotesAPI.getStats()
      ]);
      setQuotes(quotesRes.data);
      setClients(clientsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les données',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setFormData({
        ...formData,
        client_id: client.id,
        client_name: client.name,
        client_email: client.email || '',
        client_address: client.address ? `${client.address}, ${client.postal_code} ${client.city}` : '',
        client_siren: client.siren || ''
      });
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, tax_rate: 20 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData({
        ...formData,
        items: formData.items.filter((_, i) => i !== index)
      });
    }
  };

  const updateItem = (index, field, value) => {
    setFormData({
      ...formData,
      items: formData.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    });
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) =>
      sum + (item.quantity * item.unit_price), 0
    );
    const vatTotal = formData.items.reduce((sum, item) =>
      sum + (item.quantity * item.unit_price * item.tax_rate / 100), 0
    );
    return { subtotal, vatTotal, total: subtotal + vatTotal };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_name) {
      toast({ title: 'Erreur', description: 'Veuillez sélectionner ou saisir un client', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      await quotesAPI.create(formData);
      toast({
        title: 'Devis créé',
        description: `Devis pour ${formData.client_name} créé avec succès`
      });
      setShowAddDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail?.message || 'Une erreur est survenue',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      client_name: '',
      client_email: '',
      client_address: '',
      client_siren: '',
      validity_days: 30,
      payment_terms: 'Net 30',
      notes: '',
      items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 20 }]
    });
  };

  const handleStatusChange = async (quoteId, status) => {
    try {
      await quotesAPI.updateStatus(quoteId, status);
      toast({ title: 'Statut mis à jour' });
      loadData();
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleConvert = async (quote) => {
    setConvertConfirm({ open: true, quote });
  };

  const confirmConvert = async () => {
    const quote = convertConfirm.quote;
    if (!quote) return;

    setConverting(true);
    try {
      const response = await quotesAPI.convertToInvoice(quote.id);
      toast({
        title: 'Conversion réussie',
        description: response.data.message
      });
      loadData();
      setConvertConfirm({ open: false, quote: null });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de convertir ce devis',
        variant: 'destructive'
      });
    } finally {
      setConverting(false);
    }
  };

  const handleDelete = async (quote) => {
    setDeleteConfirm({ open: true, quote });
  };

  const confirmDelete = async () => {
    const quote = deleteConfirm.quote;
    if (!quote) return;

    setDeleting(true);
    try {
      await quotesAPI.delete(quote.id);
      toast({ title: 'Devis supprimé' });
      loadData();
      setDeleteConfirm({ open: false, quote: null });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de supprimer',
        variant: 'destructive'
      });
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { label: 'Brouillon', className: 'bg-slate-100 text-slate-700' },
      sent: { label: 'Envoyé', className: 'bg-blue-100 text-blue-700' },
      accepted: { label: 'Accepté', className: 'bg-green-100 text-green-700' },
      rejected: { label: 'Refusé', className: 'bg-red-100 text-red-700' },
      expired: { label: 'Expiré', className: 'bg-orange-100 text-orange-700' },
      converted: { label: 'Converti', className: 'bg-purple-100 text-purple-700' }
    };
    const { label, className } = config[status] || config.draft;
    return <Badge className={className}>{label}</Badge>;
  };

  const totals = calculateTotals();

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/smartfacture')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Mes Devis</h1>
            <p className="text-slate-500 text-sm">Créez des devis et convertissez-les en factures</p>
          </div>
        </div>

        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau devis
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Créer un devis</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Client Selection */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-700">Client</h4>
                {clients.length > 0 && (
                  <div>
                    <Label>Sélectionner un client existant</Label>
                    <Select onValueChange={handleClientSelect}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir un client..." />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(client => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name} {client.siren ? `(${client.siren})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nom du client *</Label>
                    <Input
                      value={formData.client_name}
                      onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                      placeholder="Nom ou raison sociale"
                      required
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                      placeholder="client@exemple.fr"
                    />
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-slate-700">Lignes du devis</h4>
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <Plus className="h-4 w-4 mr-1" /> Ajouter
                  </Button>
                </div>

                {formData.items.map((item, index) => (
                  <div key={index} className="flex items-end space-x-3 p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <Label>Description</Label>
                      <Input
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        placeholder="Service ou produit"
                      />
                    </div>
                    <div className="w-20">
                      <Label>Qté</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 1)}
                      />
                    </div>
                    <div className="w-28">
                      <Label>Prix HT</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="w-20">
                      <Label>TVA %</Label>
                      <Input
                        type="number"
                        min="0"
                        value={item.tax_rate}
                        onChange={(e) => updateItem(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    {formData.items.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between text-sm mb-1">
                  <span>Sous-total HT</span>
                  <span>{totals.subtotal.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span>TVA</span>
                  <span>{totals.vatTotal.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total TTC</span>
                  <span>{totals.total.toFixed(2)} €</span>
                </div>
              </div>

              {/* Options */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Validité (jours)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.validity_days}
                    onChange={(e) => setFormData({...formData, validity_days: parseInt(e.target.value) || 30})}
                  />
                </div>
                <div>
                  <Label>Conditions de paiement</Label>
                  <Select
                    value={formData.payment_terms}
                    onValueChange={(v) => setFormData({...formData, payment_terms: v})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Net 30">Net 30 jours</SelectItem>
                      <SelectItem value="Net 15">Net 15 jours</SelectItem>
                      <SelectItem value="Comptant">Comptant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Conditions particulières..."
                  rows={2}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FilePlus2 className="h-4 w-4 mr-2" />}
                  Créer le devis
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-800">{stats.total_quotes}</div>
              <div className="text-sm text-slate-500">Total devis</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600">{stats.by_status?.converted || 0}</div>
              <div className="text-sm text-slate-500">Convertis</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-600">{stats.conversion_rate}%</div>
              <div className="text-sm text-slate-500">Taux conversion</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-800">{stats.converted_amount?.toFixed(0) || 0} €</div>
              <div className="text-sm text-slate-500">Montant converti</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quotes List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : quotes.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">Aucun devis</h3>
            <p className="text-slate-500 text-sm mb-4">Créez votre premier devis</p>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau devis
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {quotes.map((quote) => (
            <Card key={quote.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-slate-800">{quote.quote_number}</span>
                        {getStatusBadge(quote.status)}
                      </div>
                      <div className="text-sm text-slate-500">{quote.client_name}</div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-6">
                    <div className="text-right">
                      <div className="font-semibold text-slate-800">{quote.grand_total?.toFixed(2)} €</div>
                      <div className="text-xs text-slate-500 flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        Valide jusqu'au {new Date(quote.valid_until).toLocaleDateString('fr-FR')}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {quote.status === 'draft' && (
                        <Button size="sm" variant="outline" onClick={() => handleStatusChange(quote.id, 'sent')}>
                          <Send className="h-4 w-4 mr-1" /> Envoyer
                        </Button>
                      )}
                      {quote.status === 'sent' && (
                        <>
                          <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleStatusChange(quote.id, 'accepted')}>
                            <CheckCircle className="h-4 w-4 mr-1" /> Accepté
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleStatusChange(quote.id, 'rejected')}>
                            <XCircle className="h-4 w-4 mr-1" /> Refusé
                          </Button>
                        </>
                      )}
                      {(quote.status === 'accepted' || quote.status === 'draft' || quote.status === 'sent') && quote.status !== 'converted' && (
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleConvert(quote)}>
                          <ArrowRightCircle className="h-4 w-4 mr-1" /> Convertir
                        </Button>
                      )}
                      {quote.status !== 'converted' && (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(quote)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Confirmation de suppression */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open, quote: open ? deleteConfirm.quote : null })}
        title="Supprimer ce devis ?"
        description={`Êtes-vous sûr de vouloir supprimer le devis "${deleteConfirm.quote?.quote_number}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        onConfirm={confirmDelete}
        loading={deleting}
        variant="destructive"
      />

      {/* Confirmation de conversion */}
      <ConfirmDialog
        open={convertConfirm.open}
        onOpenChange={(open) => setConvertConfirm({ open, quote: open ? convertConfirm.quote : null })}
        title="Convertir en facture ?"
        description={`Convertir le devis "${convertConfirm.quote?.quote_number}" en facture ? Le devis sera marqué comme converti.`}
        confirmText="Convertir"
        cancelText="Annuler"
        onConfirm={confirmConvert}
        loading={converting}
        variant="default"
      />
    </div>
  );
};

export default QuotesPage;
